import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ModalBottomButtonContainer } from './Styles';
import { DangerButton, SuccessButton } from '../connectedForm/Button';
import ModalContext from '../Context/ModalContext';
import QueryDropdown from '../connectedForm/QueryDropdown';
import * as entities from '../../graphql/entities';
import { BlurTextField } from '../connectedForm/inputComponents';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { Stack } from '@fluentui/react';
import {
  DocumentCard,
  DocumentCardDetails,
  DocumentCardTitle,
  DocumentCardType,
} from '@fluentui/react/lib/DocumentCard';
import { BasicHeader } from '../../actions/BasicHeader';
import Fieldset from '../connectedForm/Fieldset';
import { BlurTextFieldType } from '../connectedForm/BlurTextField/BlurTextField';
import Legend from '../connectedForm/Legend';
import {
  AlertType,
  IBank,
  INewPaymentLine,
  IPayment,
  IPaymentLine,
  IPaymentMethod,
  ISalesOrder,
  Maybe,
  ReceiptOrRefund,
} from '../../graphql/schema/p4types';
import { useDispatch } from 'react-redux';
import { usePrice } from '../connectedForm/Price';
import DateField from '../../components/connectedForm/DateInput/DateInput';
import { format } from 'date-fns';
import FluentModal from './FluentModal';
import { useP4ToastController } from '../../views/system/NotificationsArea';

const PaymentCard = ({
  payment,
  onPaymentClick,
}: {
  payment: IPayment;
  onPaymentClick: (payment: IPayment) => void;
}) => {
  const paymentLine = payment.paymentLines?.[0] as IPaymentLine;

  const paymentValueString = usePrice(paymentLine.paymentValue);
  const refundValueString = usePrice(payment.totalRefundValue);

  const totalInfo: string =
    payment.totalRefundValue > 0 ? `${paymentValueString} (${refundValueString} refunded)` : `${paymentValueString}`;

  const priceStyleOverride = payment.totalRefundValue > 0 ? { root: { height: '61px' } } : undefined;

  return (
    <div data-testid={`add-refund-select-payment-${paymentLine.reference}-${totalInfo}`}>
      <DocumentCard
        type={DocumentCardType.compact}
        onClick={() => onPaymentClick(payment)}
        styles={{ root: { width: '200px' } }}
      >
        <DocumentCardDetails>
          <DocumentCardTitle title={totalInfo} shouldTruncate={false} styles={priceStyleOverride} />
          <DocumentCardTitle
            title={paymentLine.paymentMethod?.description || 'Payment'}
            shouldTruncate
            showAsSecondaryTitle
          />
          {paymentLine.reference ? (
            <DocumentCardTitle title={paymentLine.reference} shouldTruncate showAsSecondaryTitle />
          ) : null}
        </DocumentCardDetails>
      </DocumentCard>
    </div>
  );
};

const SalesOrderRefundModal = ({ isOpen }: { isOpen: boolean }) => {
  const dispatch = useDispatch();
  const { dispatchSimpleToast } = useP4ToastController();

  const connectedForm = useContext(ConnectedFormContext);
  const salesOrder = connectedForm.values as ISalesOrder;
  const payments = salesOrder.payments;
  const [outstandingPayments, fullyRefundedPayments] = useMemo<Array<Array<Maybe<IPayment>>>>(() => {
    const outstanding: Array<Maybe<IPayment>> = [];
    const fullyRefunded: Array<Maybe<IPayment>> = [];
    payments?.forEach((payment) => {
      if (!payment) {
        return;
      }
      if (payment.totalValue > 0) {
        outstanding.push(payment);
      } else if (payment.totalRefundValue > 0) {
        fullyRefunded.push(payment);
      }
    });
    return [outstanding, fullyRefunded];
  }, [payments]);

  // context
  const { onClose } = useContext(ModalContext);

  // state
  const [sourcePayment, setSourcePayment] = useState<Maybe<IPayment>>(null);
  const [paymentMethod, setPaymentMethod] = useState<Maybe<IPaymentMethod>>(null);
  const [bankAccount, setBankAccount] = useState<Maybe<IBank>>(null);
  const [reference, setReference] = useState<string>('');
  const [refundValue, setRefundValue] = useState<number | string>(0); // May be a string when set from BlurTextField, but should always represent a number.
  const [refundDate, setRefundDate] = useState<string | null>(format(new Date(), 'yyyy-MM-dd'));

  // effects
  useEffect(() => {
    if (isOpen && outstandingPayments.length === 0) {
      onClose?.();
      const alertMessage =
        fullyRefundedPayments.length > 0
          ? 'Previous payments were fully refunded, none were found to refund'
          : 'Only an active Sales Order with payments can be refunded';
      dispatchSimpleToast('No payments found', alertMessage, AlertType.Warning);
    }
  }, [dispatchSimpleToast, outstandingPayments.length, fullyRefundedPayments.length, isOpen, onClose]);

  const onPaymentClick = (payment: IPayment) => {
    setSourcePayment(payment);
    setPaymentMethod(payment.paymentLines?.[0]?.paymentMethod as IPaymentMethod);
    setBankAccount(payment.paymentLines?.[0]?.bankAccount as IBank);
    setReference(payment.paymentLines?.[0]?.reference as string);
    setRefundValue(payment.totalValue);
  };

  const onConfirm = () => {
    onClose?.();

    const refund: INewPaymentLine = {
      receiptOrRefund: ReceiptOrRefund.Refund,
      paymentMethod,
      bankAccount,
      reference,
      paymentValue: refundValue,
      payment: sourcePayment,
      salesReceipt: sourcePayment?.paymentLines?.[0]?.salesReceipt || null,
      receiptDate: refundDate,
    };

    setSourcePayment(null);
    setPaymentMethod(null);
    setBankAccount(null);
    setReference('');
    setRefundValue('');

    dispatch(connectedForm.change('newPaymentLines', [refund]));
  };

  const valid =
    (sourcePayment &&
      paymentMethod &&
      bankAccount &&
      refundValue &&
      parseFloat(refundValue as string) > 0.0 &&
      parseFloat(refundValue as string) <= sourcePayment.totalValue) ||
    0;

  return (
    <FluentModal isOpen={isOpen} styles={{ main: { minWidth: '700px', paddingBottom: '100px' } }}>
      <BasicHeader>Add Refund</BasicHeader>
      <Fieldset cols={1}>
        <Legend label={'Select Payment'} />
        <div style={{ overflowX: 'auto' }}>
          <Stack horizontal={true}>
            {outstandingPayments?.map((payment) => (
              <PaymentCard key={payment!.uuid!} payment={payment!} onPaymentClick={onPaymentClick} />
            ))}
          </Stack>
        </div>
      </Fieldset>
      <Fieldset cols={1}>
        <Legend label={'Details'} />
        <QueryDropdown
          data-testid={'refund-modal-payment-method'}
          {...entities.PaymentMethod.dropdown}
          label="Payment Method"
          onChange={(value: Maybe<IPaymentMethod>) => {
            setPaymentMethod(value);
            setBankAccount(value?.bank);
          }}
          value={paymentMethod}
          mandatory={true}
          readOnly={true}
        />
        <QueryDropdown
          data-testid={'refund-modal-bank-account'}
          {...entities.Bank.dropdown}
          label="Bank Account"
          onChange={setBankAccount}
          value={bankAccount}
          mandatory={true}
          readOnly={true}
        />
        <BlurTextField
          label="Reference"
          value={reference}
          onChange={setReference}
          enableOnChange
          data-testid={'add-refund-reference'}
        />
        <BlurTextField
          label="Refund Amount"
          value={refundValue}
          onChange={setRefundValue}
          mandatory={true}
          type={BlurTextFieldType.Currency}
          decimals={2}
          enableOnChange
          data-testid={'add-refund-refund-amount'}
        />
        <DateField
          label="Refund Date"
          value={refundDate}
          onChange={setRefundDate}
          data-testid="sales-order-refund-date"
        />
      </Fieldset>
      <ModalBottomButtonContainer>
        <DangerButton data-testid="salesOrderRefund-cancel" onClick={onClose}>
          Cancel
        </DangerButton>
        <SuccessButton data-testid="salesOrderRefund-confirm" onClick={onConfirm} disabled={!valid}>
          Confirm
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default SalesOrderRefundModal;
